import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './EmojiDetailsModal.css';

const EmojiDetailsModal = ({ 
  emoji, 
  isOpen, 
  onClose, 
  emojiData = {},
  trendingData = [],
  onAddToWatchlist = null,
  trackedEmojis = [],
  getServerTime = null,
  sessionStartTime = null
}) => {
  const modalRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [shareConfirm, setShareConfirm] = useState(false);
  const [fetchedStats, setFetchedStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Bottom-sheet swipe-to-dismiss (mobile only)
  const dragStartY = useRef(null);
  const dragCurrentY = useRef(0);
  const isDragging = useRef(false);

  const handleTouchStart = (e) => {
    // Only initiate drag from the handle area (top 40px of modal)
    const modal = modalRef.current;
    if (!modal) return;
    const rect = modal.getBoundingClientRect();
    const touchY = e.touches[0].clientY;
    if (touchY - rect.top > 40) return; // only drag from handle zone
    dragStartY.current = touchY;
    isDragging.current = true;
    modal.style.transition = 'none';
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || dragStartY.current === null) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    if (dy < 0) { dragCurrentY.current = 0; return; } // don't drag upward
    dragCurrentY.current = dy;
    if (modalRef.current) {
      modalRef.current.style.transform = `translateY(${dy}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const modal = modalRef.current;
    if (!modal) return;
    modal.style.transition = 'transform 0.25s ease';
    if (dragCurrentY.current > 120) {
      // Dismiss — slide down fully, then call onClose
      modal.style.transform = 'translateY(100%)';
      setTimeout(onClose, 250);
    } else {
      // Snap back
      modal.style.transform = 'translateY(0)';
    }
    dragStartY.current = null;
    dragCurrentY.current = 0;
  };

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    // Use setTimeout to avoid immediate close on the click that opened it
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fetch full stats from API if not available locally
  useEffect(() => {
    if (!isOpen || !emoji) {
      setFetchedStats(null);
      setIsLoadingStats(false);
      return;
    }

    // Check if we already have full stats from trendingData or emojiData
    const trendingInfo = trendingData && trendingData.length > 0 ? trendingData.find(t => t.emoji === emoji) : null;
    const emojiInfo = emojiData && typeof emojiData === 'object' && emojiData[emoji] ? emojiData[emoji] : null;
    
    const hasFullStats = (trendingInfo && trendingInfo.count > 0) ||
                         (emojiInfo && typeof emojiInfo === 'object' && emojiInfo.count > 0);
    
    // Only fetch if we don't have full stats
    if (!hasFullStats && !isLoadingStats && !fetchedStats) {
      setIsLoadingStats(true);
      const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      fetch(`${apiUrl}/api/emoji-stats/${encodeURIComponent(emoji)}`)
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch stats');
          return res.json();
        })
        .then(data => {
          setFetchedStats(data);
          setIsLoadingStats(false);
        })
        .catch(err => {
          console.warn('Failed to fetch emoji stats:', err);
          setIsLoadingStats(false);
        });
    }
  }, [isOpen, emoji, trendingData, emojiData, isLoadingStats, fetchedStats]);

  if (!isOpen || !emoji) return null;

  // Get count and postCount from emojiData or trendingData
  let count = 0;
  let postCount = 0;
  
  // Check trendingData first (it's more up-to-date)
  const trendingInfo = trendingData && trendingData.length > 0 ? trendingData.find(t => t.emoji === emoji) : null;
  if (trendingInfo) {
    count = trendingInfo.count || 0;
    postCount = trendingInfo.postCount || 0;
  }
  
  // Then check emojiData (fallback or for additional data)
  if (emojiData && typeof emojiData === 'object' && emojiData[emoji]) {
    const data = emojiData[emoji];
    if (count === 0) {
      if (typeof data === 'number') {
        count = data;
      } else if (data && typeof data === 'object') {
        count = data.count !== undefined ? data.count : 0;
      }
    }
    if (postCount === 0) {
      if (typeof data !== 'number' && data && typeof data === 'object') {
        postCount = data.postCount !== undefined ? data.postCount : 0;
      }
    }
  }
  
  // Use fetched stats if available
  if (fetchedStats) {
    if (fetchedStats.count !== undefined) {
      count = fetchedStats.count;
    }
    if (fetchedStats.postCount !== undefined) {
      postCount = fetchedStats.postCount;
    }
  }
  
  
  // If count > 0 but postCount is 0 or missing, infer minimum postCount = 1
  // (if an emoji was used, it must have appeared in at least one post)
  if (count > 0 && postCount === 0) {
    postCount = 1;
  }
  
  // Special case: if count is 0 but the emoji was passed to the modal, ensure minimum count of 1
  // This handles edge cases where data might be stale or incomplete
  // If an emoji is being displayed in the modal, it must have been seen at least once
  if (count === 0) {
    // Only set to 1 if we have some indication the emoji exists (in data or was explicitly passed)
    // This prevents showing "1 use" for emojis that truly don't exist
    if (trendingInfo || (emojiData && typeof emojiData === 'object' && emojiData[emoji])) {
      count = 1;
      if (postCount === 0) {
        postCount = 1;
      }
    } else {
      // If emoji truly has no data, log a warning but don't fake the count
      // The warning was already logged above
    }
  }
  
  const velocity = trendingInfo?.velocity !== undefined ? trendingInfo.velocity : undefined;
  const postRate = trendingInfo?.postRate !== undefined ? trendingInfo.postRate : (trendingInfo ? 0 : undefined);
  const trend = trendingInfo?.trend || 'stable';
  const isTracked = trackedEmojis && trackedEmojis.includes(emoji);
  
  // Calculate uses per post ratio
  const usesPerPost = postCount > 0 ? (count / postCount) : null;
  const usesPerPostFormatted = usesPerPost ? usesPerPost.toFixed(2) : null;

  // Format number
  const formatNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return '0';
    if (!isFinite(num)) return num > 0 ? '∞' : '-∞';
    if (num < 0) return '-' + formatNumber(-num);
    
    const absNum = Math.abs(num);
    if (absNum >= 1000000000000000) return (absNum / 1000000000000000).toFixed(1).replace(/\.0$/, '') + 'Qa';
    if (absNum >= 1000000000000) return (absNum / 1000000000000).toFixed(1).replace(/\.0$/, '') + 'T';
    if (absNum >= 1000000000) return (absNum / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (absNum >= 1000000) return (absNum / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (absNum >= 1000) return (absNum / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return absNum.toLocaleString();
  };

  // Helper function to split number and suffix for better alignment
  const renderFormattedNumber = (num) => {
    const valueStr = String(formatNumber(num)).trim();
    let match = valueStr.match(/^([\d.,]+)(Qa)$/);
    if (!match) {
      match = valueStr.match(/^([\d.,]+)([KMBT])$/);
    }
    if (match && match[2]) {
      const [, number, suffix] = match;
      return (
        <>
          <span className="modal-number-part">{number}</span>
          <span className="modal-number-suffix">{suffix}</span>
        </>
      );
    }
    return valueStr;
  };

  // Copy emoji to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(emoji);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emoji;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Failed to copy:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  // Search on X
  const handleSearchX = () => {
    const encodedEmoji = encodeURIComponent(emoji);
    const searchUrl = `https://x.com/search?q=${encodedEmoji}&src=live&f=live`;
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  };

  // Build share text
  const getShareText = () => {
    const rankText = rank ? ` (#${rank})` : '';
    return `${emoji} has ${formatNumber(count)} uses across ${formatNumber(postCount)} posts on X right now${rankText}`;
  };

  // Post on X — opens X's compose intent (auto-fills tweet if logged in)
  const handlePostOnX = () => {
    const text = getShareText();
    const url = 'https://xemojis.app';
    const intentUrl = `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(intentUrl, '_blank', 'noopener,noreferrer,width=550,height=420');
  };

  // Copy share text to clipboard (fallback)
  const handleCopyShare = async () => {
    const text = `${getShareText()} — xemojis.app`;
    try {
      await navigator.clipboard.writeText(text);
      setShareConfirm(true);
      setTimeout(() => setShareConfirm(false), 2000);
    } catch (e) {
      // last resort: prompt
      window.prompt('Copy this:', text);
    }
  };

  // Add to watchlist
  const handleAddToWatchlist = () => {
    if (onAddToWatchlist && !isTracked) {
      onAddToWatchlist(emoji);
    }
  };

  // Get rank (approximate from count or trending data)
  const getRank = () => {
    if (trendingData && trendingData.length > 0) {
      const sorted = [...trendingData].sort((a, b) => (b.count || 0) - (a.count || 0));
      const index = sorted.findIndex(t => t.emoji === emoji);
      if (index >= 0) return index + 1;
    }
    // Fallback: estimate rank from emojiData if available
    if (emojiData && Object.keys(emojiData).length > 0) {
      const sorted = Object.entries(emojiData)
        .sort(([, a], [, b]) => (b || 0) - (a || 0));
      const index = sorted.findIndex(([e]) => e === emoji);
      if (index >= 0) return index + 1;
    }
    return null;
  };

  const rank = getRank();

  const modalContent = (
    <div className="emoji-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div
        className="emoji-modal"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="emoji-modal-drag-handle" aria-hidden="true" />
        <button className="emoji-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        
        <div className="emoji-modal-header">
          <div className="emoji-modal-emoji">
            {emoji}
            {usesPerPost && usesPerPost > 10 && (
              <div className="spam-badge" title={`Possible spam: ${usesPerPost} uses per post`}>
                ⚠️ Spam
              </div>
            )}
          </div>
        </div>

        <div className="emoji-modal-actions">
          <button 
            className={`emoji-modal-action-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
          >
            {copied ? '✓ Copied!' : '📋 Copy Emoji'}
          </button>
          
          {!isTracked && onAddToWatchlist && (
            <button 
              className="emoji-modal-action-btn primary"
              onClick={handleAddToWatchlist}
            >
              ⭐ Add to Watchlist
            </button>
          )}
          
          {isTracked && (
            <button 
              className="emoji-modal-action-btn tracked"
              disabled
            >
              ⭐ In Watchlist
            </button>
          )}
          
          <button 
            className="emoji-modal-action-btn search-on-x"
            onClick={handleSearchX}
          >
            🔍 Search on X
          </button>

          <button
            className="emoji-modal-action-btn"
            onClick={handlePostOnX}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{flexShrink: 0}}>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Post on 𝕏
          </button>

          <button
            className={`emoji-modal-action-btn ${shareConfirm ? 'copied' : ''}`}
            onClick={handleCopyShare}
          >
            {shareConfirm ? '✓ Copied to clipboard!' : '📋 Copy Stats'}
          </button>
        </div>

        <div className="emoji-modal-stats">
          {/* Usage Stats Section */}
          <div className="emoji-stat-section">
            <div className="emoji-stat-section-title">📊 Usage</div>
            <div className="emoji-stat-items">
              <div className="emoji-stat-item">
                <div className="emoji-stat-label">Total Uses</div>
                <div className="emoji-stat-value">{renderFormattedNumber(count)}</div>
              </div>
              
              {postCount > 0 && (
                <div className="emoji-stat-item">
                  <div className="emoji-stat-label">Posts</div>
                  <div className="emoji-stat-value">{renderFormattedNumber(postCount)}</div>
                </div>
              )}
              
              {usesPerPostFormatted && (
                <div className="emoji-stat-item">
                  <div className="emoji-stat-label">Uses/Post</div>
                  <div className={`emoji-stat-value ${usesPerPost > 10 ? 'spam-warning' : ''}`}>
                    {usesPerPostFormatted}
                    {usesPerPost > 10 && ' ⚠️'}
                  </div>
                </div>
              )}
              
              {rank && (
                <div className="emoji-stat-item">
                  <div className="emoji-stat-label">Current Rank</div>
                  <div className="emoji-stat-value">#{rank}</div>
                </div>
              )}
            </div>
          </div>

          {/* Growth Stats Section */}
          {(velocity !== undefined || postRate !== undefined) && (
            <div className="emoji-stat-section">
              <div className="emoji-stat-section-title">📈 Growth</div>
              <div className="emoji-stat-items">
                {velocity !== undefined && (
                  <div className="emoji-stat-item">
                    <div className="emoji-stat-label">Velocity</div>
                    <div className={`emoji-stat-value ${trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : ''}`}>
                      {velocity >= 0 ? '+' : ''}{velocity.toFixed(1)}/s
                    </div>
                  </div>
                )}
                
                {postRate !== undefined && (
                  <div className="emoji-stat-item">
                    <div className="emoji-stat-label">Post Rate</div>
                    <div className="emoji-stat-value positive">
                      {postRate > 0 ? `${postRate.toFixed(2)} posts/s` : '0.00 posts/s'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );

  // Render modal using portal to document.body to avoid parent overflow issues
  return createPortal(modalContent, document.body);
};

export default EmojiDetailsModal;
